import { applyTacticalOverrides } from './tacticalEffects'
import { expectedMatchMargin, seededNormal } from './matchProbability'
import { commitCurrentMatchContext } from './matchContext'
import { buildWeeklyDevelopmentOutcomes } from './progression'
import { applyDevelopmentProgress, tacticalCohesionGain } from './progression'
import { archiveCurrentResearch, createResearchPlan } from './research'
import { selectedPlayerForStaffSlot } from './selection'
import { advanceSeasonFinalsWeek, completeSeasonFinals, createSeasonState, fixtureForTeam, initialiseSeason, prepareFinalsWeek, prepareSeasonRound, recordAiRound, recordFinalsResult, recordLeagueRound, simulateLeagueFixture } from './season'
import { createCareerHistory, syncCareerHistory } from './history'
import { createClubStrategy, updateClubStrategyAfterRound } from './clubStrategy'
import { createDefaultSalaryCap, hydrateContractState, processNegotiationRound } from './contracts'
import { advanceFacilityConstruction, createFacilitiesState, hydrateFacilities } from './facilities'
import { createStaffMarketState, hydrateStaffMarket, processStaffPoaching } from './staffMarket'
import { createInitialCashFlowLedger, createInitialSponsorshipState, hydrateSponsorship, processWeeklyRevenue } from './sponsorship'
import { createInitialLeftFieldState, delegateLeftFieldEvent, hasPendingLeftFieldEvent, prepareLeftFieldEvent } from './leftField'
import { applyWeeklyInjuries, repairUnavailableSelections, teamSelectionIssue, withGeneratedInjuries } from './injuries'
import { isTeamAliveInFinals } from './competition'
import { createPlayerDevelopmentState } from './progression'
import { createRecruitmentState } from './recruitment'

import { stableHash } from './deterministic'
import { isReadyForMatch } from './validation'
import type {
  ChoiceLetter,
  ConsequenceReport,
  Decision,
  DecisionId,
  GamePhase,
  GameState,
  MatchResult,
  MatchTactic,
  Player,
  Position,
  ScheduledFixture,
  StaffMember,
  StaffPlan,
  TeamPlayer,
  TeamSelection,
} from './types'

export const PLAYER_AGES: Record<string, number> = {
  'Tom Alder': 24, 'Finn Calder': 27, 'Patrick Sloane': 31, 'Theo Marr': 25,
  'Eli Ward': 28, 'Reid Fallon': 22, 'Noah Venn': 23, 'Kieran Moss': 26,
  'Seth Wren': 29, 'Callan Frost': 24, 'Lachlan Rook': 28, 'Damon Quill': 21,
  'Milo Trent': 20, 'Darcy North': 30, 'Lucas Grey': 32, 'Rowan Pike': 29,
  'Marcus Vale': 27, 'Jory Beck': 26, 'Heath Mercer': 30, 'Hugh Renn': 23,
  'Ben Armit': 28, 'Owen Hart': 21, 'Ari Dane': 19, 'Sam Tor': 20,
}

const PLAYER_ROLES: Record<string, { role: string; position: Position }> = {
  'Tom Alder': { role: 'key defender', position: 'FB' },
  'Finn Calder': { role: 'rebounding defender', position: 'HB' },
  'Patrick Sloane': { role: 'experienced leader', position: 'C' },
  'Theo Marr': { role: 'defensive wing', position: 'HB' },
  'Eli Ward': { role: 'inside midfielder', position: 'C' },
  'Reid Fallon': { role: 'burst midfielder', position: 'INT' },
  'Noah Venn': { role: 'half-forward', position: 'HF' },
  'Kieran Moss': { role: 'marking forward', position: 'F' },
  'Seth Wren': { role: 'ruck', position: 'FOL' },
  'Callan Frost': { role: 'wing', position: 'HB' },
  'Lachlan Rook': { role: 'key forward', position: 'F' },
  'Damon Quill': { role: 'small forward', position: 'HF' },
  'Milo Trent': { role: 'midfielder', position: 'INT' },
  'Darcy North': { role: 'defender', position: 'FB' },
  'Lucas Grey': { role: 'veteran leader', position: 'C' },
  'Rowan Pike': { role: 'ruck-rover', position: 'FOL' },
  'Marcus Vale': { role: 'inside midfielder', position: 'INT' },
  'Jory Beck': { role: 'utility', position: 'INT' },
  'Heath Mercer': { role: 'inside midfielder', position: 'INT' },
  'Hugh Renn': { role: 'rebounding defender', position: 'HB' },
  'Ben Armit': { role: 'forward', position: 'F' },
  'Owen Hart': { role: 'midfielder', position: 'C' },
  'Ari Dane': { role: 'inside midfielder', position: 'INT' },
  'Sam Tor': { role: 'forward', position: 'HF' },
}

const defaultAbilities = (): Player['abilities'] => ({
  kicking: 'League Standard', handball: 'League Standard', marking: 'League Standard',
  groundBall: 'League Standard', tackling: 'League Standard', ruckWork: 'League Standard',
  goalSense: 'League Standard', defensiveCraft: 'League Standard', contestedAbility: 'League Standard',
  speed: 'League Standard', acceleration: 'League Standard', endurance: 'League Standard',
  strength: 'League Standard', agility: 'League Standard', leap: 'League Standard',
  recovery: 'League Standard', durability: 'League Standard', positioning: 'League Standard',
  decisionMaking: 'League Standard', anticipation: 'League Standard', spatialAwareness: 'League Standard',
  tacticalDiscipline: 'League Standard', adaptability: 'League Standard', composure: 'League Standard',
  professionalism: 'League Standard', coachability: 'League Standard', competitiveness: 'League Standard',
  resilience: 'League Standard', loyalty: 'League Standard', ambition: 'League Standard',
  selflessness: 'League Standard', leadership: 'League Standard', consistency: 'League Standard',
  bigGameTemperament: 'League Standard',
})

const INITIAL_TEAM: TeamSelection = {
  backs: [
    { playerId: 'tom-alder', position: 'FB', role: 'key defender' },
    { playerId: 'darcy-north', position: 'FB', role: 'defender' },
    { playerId: 'finn-calder', position: 'FB', role: 'rebounding defender' },
  ],
  halfBacks: [
    { playerId: 'theo-marr', position: 'HB', role: 'defensive wing' },
    { playerId: 'callan-frost', position: 'HB', role: 'wing' },
    { playerId: 'hugh-renn', position: 'HB', role: 'rebounding defender' },
  ],
  centre: [
    { playerId: 'patrick-sloane', position: 'C', role: 'experienced leader' },
    { playerId: 'lucas-grey', position: 'C', role: 'veteran leader' },
    { playerId: 'owen-hart', position: 'C', role: 'midfielder' },
  ],
  halfForwards: [
    { playerId: 'noah-venn', position: 'HF', role: 'half-forward' },
    { playerId: 'damon-quill', position: 'HF', role: 'small forward' },
    { playerId: 'jory-beck', position: 'HF', role: 'utility' },
  ],
  forwards: [
    { playerId: 'kieran-moss', position: 'F', role: 'marking forward' },
    { playerId: 'lachlan-rook', position: 'F', role: 'key forward' },
    { playerId: 'ben-armit', position: 'F', role: 'forward' },
  ],
  followers: [
    { playerId: 'seth-wren', position: 'FOL', role: 'ruck' },
    { playerId: 'rowan-pike', position: 'FOL', role: 'ruck-rover' },
    { playerId: 'marcus-vale', position: 'FOL', role: 'inside midfielder' },
  ],
  interchange: [
    { playerId: 'reid-fallon', position: 'INT', role: 'burst midfielder' },
    { playerId: 'milo-trent', position: 'INT', role: 'midfielder' },
    { playerId: 'heath-mercer', position: 'INT', role: 'inside midfielder' },
    { playerId: 'eli-ward', position: 'INT', role: 'inside midfielder' },
  ],
  emergencies: [
    { playerId: 'ari-dane', position: 'EMG', role: 'inside midfielder' },
    { playerId: 'sam-tor', position: 'EMG', role: 'forward' },
  ],
}

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T
const initialSeniorIds = new Set(Object.entries(INITIAL_TEAM)
  .filter(([group]) => group !== 'emergencies')
  .flatMap(([, players]) => (players as TeamPlayer[]).map(player => player.playerId)))

const createInitialKingsportRoster = (): Player[] => {
  return Object.entries(PLAYER_AGES).map(([name, age]) => {
    const { role, position } = PLAYER_ROLES[name]
    const id = name.toLowerCase().replaceAll(' ', '-')
    const teamEntry = Object.values(INITIAL_TEAM).flat().find(entry => entry.playerId === id)
    const abilities = defaultAbilities()
    if (name === 'Ari Dane') {
      abilities.tacticalDiscipline = 'Below Standard'
      abilities.speed = 'Above Standard'
      abilities.acceleration = 'Above Standard'
    }
    return {
      id,
      name,
      age,
      primaryRole: role,
      position,
      abilities,
      form: 55,
      fitness: 94,
      fatigue: 12,
      morale: 58,
      contractYears: 2,
      developmentFocus: null,
      isSelected: initialSeniorIds.has(id),
      positionInTeam: initialSeniorIds.has(id) ? teamEntry?.position ?? position : null,
      roleInTeam: initialSeniorIds.has(id) ? teamEntry?.role ?? role : null,
      ...(name === 'Ari Dane' ? { staffConcern: 'Approaching senior readiness; a debut trades reliability for pace and senior exposure.' } : {}),
      development: createPlayerDevelopmentState({ name, age, developmentFocus: null }),
      clubHistory: [{ clubId: 'kingsport', fromSeason: 1, arrival: 'foundation' }],
      injury: null,
      injuryHistory: [],
    }
  })
}

const INITIAL_STAFF: StaffMember[] = [
  { id: 'grant-havel', name: 'Grant Havel', role: 'Backline Coach', isSeniorCoach: false, abilities: { Coaching: 'Above Standard', Tactics: 'Above Standard', Teaching: 'League Standard', Communication: 'League Standard', Conditioning: 'Below Standard' }, developmentFocuses: ['Coaching', 'Tactics', 'Teaching', 'Communication', 'Conditioning'], teachingStyle: 'Role-specific and accountable', tacticalAlignment: 'Structured defensive system', relationships: 'Above Standard', ambition: 'League Standard', loyalty: 'Above Standard', workload: 65, reputation: 'Established assistant', poachability: 0, characteristics: ['structured'], expertise: ['defence'], philosophy: 'structured', recommendation: 'Defensive craft and role clarity.' },
  { id: 'mara-keane', name: 'Mara Keane', role: 'Development Coach', isSeniorCoach: false, abilities: { Coaching: 'Above Standard', Tactics: 'League Standard', Teaching: 'League Standard', Communication: 'Above Standard', Conditioning: 'League Standard' }, developmentFocuses: ['Coaching', 'Tactics', 'Teaching', 'Communication', 'Conditioning'], teachingStyle: 'Evidence-led and encouraging', tacticalAlignment: 'Teacher-led development', relationships: 'Above Standard', ambition: 'Above Standard', loyalty: 'League Standard', workload: 65, reputation: 'Established assistant', poachability: 0, characteristics: ['teacher-led'], expertise: ['development'], philosophy: 'teacher-led', recommendation: 'Protect Ari’s tactical-discipline pathway and earn exposure.' },
  { id: 'ivo-sayer', name: 'Ivo Sayer', role: 'Forwards Coach', isSeniorCoach: false, abilities: { Coaching: 'Above Standard', Tactics: 'Above Standard', Teaching: 'League Standard', Communication: 'Above Standard', Conditioning: 'Below Standard' }, developmentFocuses: ['Coaching', 'Tactics', 'Teaching', 'Communication', 'Conditioning'], teachingStyle: 'Role-specific and accountable', tacticalAlignment: 'Front-foot attacking system', relationships: 'Above Standard', ambition: 'Above Standard', loyalty: 'League Standard', workload: 65, reputation: 'Established assistant', poachability: 0, characteristics: ['front-foot'], expertise: ['attack'], philosophy: 'front-foot', recommendation: 'Create repeatable forward-half connection.' },
  { id: 'tessa-byrne', name: 'Tessa Byrne', role: 'Fitness Coach', isSeniorCoach: false, abilities: { Coaching: 'League Standard', Tactics: 'Below Standard', Teaching: 'League Standard', Communication: 'League Standard', Conditioning: 'Strong' }, developmentFocuses: ['Coaching', 'Tactics', 'Teaching', 'Communication', 'Conditioning'], teachingStyle: 'Availability-led and individualised', tacticalAlignment: 'Availability-first preparation', relationships: 'League Standard', ambition: 'League Standard', loyalty: 'Above Standard', workload: 65, reputation: 'Established assistant', poachability: 0, characteristics: ['availability-first'], expertise: ['conditioning'], philosophy: 'availability-first', recommendation: 'Keep the senior group available across the season.' },
]

const tacticOptions: Record<MatchTactic['id'], MatchTactic['options']> = {
  'D6.1': [
    { letter: 'A', label: 'Balanced', description: 'Retain the staff balance between territory and possession.' },
    { letter: 'B', label: 'Direct', description: 'Move the ball directly before the opposition can reset.' },
    { letter: 'C', label: 'Patient', description: 'Use patient possession to reduce turnover exposure.' },
  ],
  'D6.2': [
    { letter: 'A', label: 'Boundary/wide', description: 'Stretch the ground and use the outside release.' },
    { letter: 'B', label: 'Balanced', description: 'Use width without abandoning the corridor.' },
    { letter: 'C', label: 'Corridor/narrow', description: 'Attack through the central channel.' },
  ],
  'D6.3': [
    { letter: 'A', label: 'Preserve structure', description: 'Protect shape around the contest.' },
    { letter: 'B', label: 'Balanced', description: 'Add support without fully collapsing structure.' },
    { letter: 'C', label: 'Maximum pressure', description: 'Commit extra numbers and pressure to the contest.' },
  ],
  'D6.4': [
    { letter: 'A', label: 'Balanced', description: 'Hold a balanced defensive line.' },
    { letter: 'B', label: 'Deep', description: 'Protect space behind the defence.' },
    { letter: 'C', label: 'Aggressive', description: 'Compress the ground with a high line.' },
  ],
  'D6.5': [
    { letter: 'A', label: 'Sustainable', description: 'Manage tempo around player condition.' },
    { letter: 'B', label: 'Balanced', description: 'Use a controlled, balanced match tempo.' },
    { letter: 'C', label: 'Relentless', description: 'Drive repeat efforts and accept greater fatigue risk.' },
  ],
}

const createInitialTactics = (): MatchTactic[] => (Object.keys(tacticOptions) as MatchTactic['id'][]).map(id => ({
  id,
  dimension: id === 'D6.1' ? 'Ball movement' : id === 'D6.2' ? 'Attacking width' : id === 'D6.3' ? 'Contest numbers' : id === 'D6.4' ? 'Defensive line' : 'Tempo',
  staffChoice: 'A',
  currentChoice: 'A',
  options: clone(tacticOptions[id]),
}))

const decisionChoice = (id: ChoiceLetter, label: string, immediateEffect = 'Preserves the prepared staff baseline.') => ({
  id, label, description: label, cost: {}, immediateEffect, longTermEffect: 'The weekly plan remains deterministic.', risk: 'Known trade-offs remain visible.', isStaffRecommendation: id === 'A', isAvailable: true,
})

const createInitialDecisions = (tactics: MatchTactic[]): GameState['decisions'] => {
  const decision = (id: DecisionId, title: string, choices = [decisionChoice('A', 'Retain staff recommendation'), decisionChoice('B', 'Coach override')]): Decision => ({ id, title, description: title, choices, currentChoice: 'A', isLocked: false })
  const result = {
    D1: decision('D1', 'Weekly priorities'),
    D2: decision('D2', 'Senior team selection', [
      decisionChoice('A', 'Retain staff team'),
      decisionChoice('B', 'Select Ari Dane', 'Creates a senior opportunity for Ari while accepting reduced tactical reliability.'),
      decisionChoice('C', 'Review another player'),
      decisionChoice('D', 'Coach-edited team sheet'),
    ]),
    D3: decision('D3', 'Development allocation'),
    D4: decision('D4', 'Opposition research'),
    D5: decision('D5', 'Training plan'),
    D6: decision('D6', 'Match plan'),
    D7: decision('D7', 'Conditional instruction'),
    D8: decision('D8', 'Match commitment'),
  } as GameState['decisions']
  for (const tactic of tactics) result[tactic.id] = decision(tactic.id, tactic.dimension, tactic.options.map(option => decisionChoice(option.letter, option.label)))
  return result
}

export type AriPlanChoice = 'staff' | 'debut'

const replaceTeamPlayer = (
  players: TeamPlayer[],
  outgoingId: string,
  incoming: TeamPlayer,
): TeamPlayer[] => players.map((entry) => (entry.playerId === outgoingId ? incoming : { ...entry }))

export const createInitialState = (careerSeed = 'trial-career'): GameState => {
  const season = createSeasonState(false, 1, careerSeed)
  const numericSeed = stableHash(careerSeed)
  const players = createInitialKingsportRoster()
  const tactics = createInitialTactics()
  const instruction = conditionalInstruction('Opening opponent')
  const development = createStaffDevelopmentPlan()
  const staffPlan: StaffPlan = {
    team: clone(INITIAL_TEAM),
    development: clone(development),
    research: { decision: 'D4', points: 0 },
    training: { load: 'moderate', focus: ['system connection', 'contest shape'], recovery: ['veteran recovery'] },
    matchPlan: clone(tactics),
    conditionalInstruction: clone(instruction),
  }
  const salaryCap = createDefaultSalaryCap()
  const facilities = createFacilitiesState(10)
  const sponsorship = createInitialSponsorshipState(season.seed, 1)
  let state: GameState = {
    round: 1,
    season,
    week: undefined,
    club: {
      name: 'Kingsport Imperials',
      record: { wins: 0, losses: 0, draws: 0 },
      resources: { cash: 12, research: 6, development: 10, institutional: 54 },
      researchCapacity: 6,
      maxResearchCapacity: 12,
      weeklyResearchGeneration: 3,
      developmentCapacity: 10,
      tacticalCohesion: 53.8,
      boardObjective: 'Finals',
      players,
      seniorCoach: { id: 'senior-coach', name: 'You', role: 'Senior Coach', isSeniorCoach: true,
        abilities: { Leadership: 'League Standard', Teaching: 'League Standard', 'Tactical acumen': 'League Standard',
          'Player assessment': 'League Standard', 'Staff management': 'League Standard', Communication: 'League Standard',
          'Match-day composure': 'League Standard', 'Culture building': 'League Standard' },
        developmentFocuses: ['Leadership', 'Teaching', 'Tactical acumen', 'Player assessment', 'Staff management', 'Communication', 'Match-day composure', 'Culture building'],
        teachingStyle: 'Still forming', tacticalAlignment: 'No fixed system', relationships: 'Unproven with this group',
        ambition: 'Strong', loyalty: 'League Standard', workload: 56, reputation: 'Unproven appointment', poachability: 0, characteristics: [] },
      staff: clone(INITIAL_STAFF),
      contracts: {},
      salaryCap,
      contractNegotiations: [],
      contractHistory: [],
    },
    opponent: { name: '', record: { wins: 0, losses: 0, draws: 0 }, strengths: [], weaknesses: [], style: '', assessment: '' },
    decisions: createInitialDecisions(tactics),
    tactics,
    conditionalInstruction: instruction,
    staffPlan,
    coachDevelopment: { season: 1, totalPoints: 6, isLocked: false, allocations: [
      { coachId: 'senior-coach', focus: 'Leadership', points: 1 },
      { coachId: 'grant-havel', focus: 'Tactics', points: 1 },
      { coachId: 'mara-keane', focus: 'Teaching', points: 2 },
      { coachId: 'ivo-sayer', focus: 'Coaching', points: 1 },
      { coachId: 'tessa-byrne', focus: 'Conditioning', points: 1 },
    ] },
    researchPlan: createResearchPlan(1, '', 6),
    researchArchive: [],
    matchResult: null,
    consequenceReport: null,
    phase: 'headquarters',
    seed: numericSeed,
    recruitment: createRecruitmentState(numericSeed, 1, players),
    history: createCareerHistory(),
    clubStrategy: {} as GameState['clubStrategy'],
    facilities,
    staffMarket: createStaffMarketState(INITIAL_STAFF, season.seed, 1),
    contracts: {},
    salaryCap,
    contractNegotiations: [],
    contractHistory: [],
    sponsorship,
    cashFlowLedger: createInitialCashFlowLedger(12),
    leftField: createInitialLeftFieldState(),
  }
  state = { ...state, clubStrategy: createClubStrategy(state) }
  state = hydrateContractState(state)
  state = hydrateFacilities(state, state.facilities)
  state = hydrateStaffMarket(state, state.staffMarket)
  state = hydrateSponsorship(state, state.sponsorship)
  return prepareLeftFieldEvent(prepareSeasonRound(state, 1))
}

/**
 * Test helper: builds a valid GameState for round R with all required fields.
 * Uses createInitialState and advances to just-before round R (phase === 'headquarters').
 */
export const createTestState = (targetRound: number, careerSeed?: string): GameState => {
  const isolatedWeeklyFixture = careerSeed === undefined
  const resolvedSeed = careerSeed ?? 'canonical-v56-11'
  const pristine = createInitialState(resolvedSeed)
  let state = pristine
  const desiredRound = Math.max(1, targetRound)
  while (state.round < desiredRound) {
    if (hasPendingLeftFieldEvent(state)) state = delegateLeftFieldEvent(state)
    const resolved = resolveCurrentRound(state)
    state = resolved.phase === 'next-round' ? resolved : advanceAfterRound(resolved)
    if (state.phase === 'next-round') state = continueToNextRound(state)
  }
  if (hasPendingLeftFieldEvent(state)) state = delegateLeftFieldEvent(state)
  if (desiredRound === 1 || !isolatedWeeklyFixture) return state

  // Test fixtures begin each requested week from the canonical weekly baseline,
  // while retaining the genuinely simulated fixture results and club record.
  // This keeps the helper useful for isolated weekly-domain tests: prior rounds
  // determine the schedule/ladder, but do not pre-spend the week under test or
  // pre-develop its players before an assertion exercises that transition.
  const roundKey = `s${state.season?.season ?? 1}-r${state.round}`
  const canonicalTeam = clone(INITIAL_TEAM)
  const staffPlan = { ...state.staffPlan, team: canonicalTeam }
  const weeklyDefaults = state.week?.staffDefaults ?? state.staffPlan
  return {
    ...state,
    club: {
      ...state.club,
      resources: { ...pristine.club.resources },
      researchCapacity: pristine.club.researchCapacity,
      tacticalCohesion: pristine.club.tacticalCohesion,
      players: clone(pristine.club.players),
    },
    decisions: { ...state.decisions, D2: { ...state.decisions.D2, currentChoice: 'A', isLocked: false } },
    staffPlan,
    week: state.week ? { ...state.week, staffDefaults: { ...weeklyDefaults, team: clone(canonicalTeam) } } : state.week,
    researchPlan: { ...state.researchPlan, openingCapacity: pristine.club.researchCapacity },
    researchArchive: [],
    recruitment: clone(pristine.recruitment),
    history: createCareerHistory(),
    facilities: clone(pristine.facilities),
    staffMarket: clone(pristine.staffMarket),
    sponsorship: { ...clone(pristine.sponsorship), processedRounds: [roundKey] },
    cashFlowLedger: clone(pristine.cashFlowLedger),
    clubStrategy: createClubStrategy({ ...state, club: { ...state.club, resources: { ...pristine.club.resources }, players: clone(pristine.club.players) } }),
  }
}

export const selectAriPlan = (state: GameState, choice: AriPlanChoice): GameState => {
  const debut = choice === 'debut'
  const ariId = 'ari-dane'
  const heathId = 'heath-mercer'
  if (debut && state.club.players.find(player => player.id === ariId)?.injury) return state
  // Block Ari debut if Heath Mercer is injured (no healthy replacement available)
  if (debut && state.club.players.find(player => player.id === heathId)?.injury) return state

  // Use canonical staff defaults for restoration to preserve healthy replacements
  // rather than hard-coding Heath Mercer. The staffDefaults team is the repaired
  // week baseline generated at week preparation and must survive Coach overrides.
  const canonicalInterchange = state.week?.staffDefaults?.team.interchange
    ? state.week.staffDefaults.team.interchange
    : state.staffPlan.team.interchange

  const selectedInterchange = debut
    ? replaceTeamPlayer(state.staffPlan.team.interchange, heathId, {
        playerId: ariId,
        position: 'INT',
        role: 'inside midfielder',
      })
    : replaceTeamPlayer(state.staffPlan.team.interchange, ariId, {
        playerId: heathId,
        position: 'INT',
        role: 'inside midfielder',
      })

  // When restoring staff plan, use the canonical staff defaults interchange
  // which preserves any healthy replacement made during repair.
  const restoredInterchange = debut
    ? selectedInterchange
    : canonicalInterchange.map(entry => ({ ...entry }))

  // Restore emergencies from canonical defaults when returning to staff plan
  const canonicalEmergencies = state.week?.staffDefaults?.team.emergencies
    ? state.week.staffDefaults.team.emergencies
    : state.staffPlan.team.emergencies

  const restoredEmergencies = debut
    ? state.staffPlan.team.emergencies.filter((entry) => entry.playerId !== ariId).map((entry) => ({ ...entry }))
    : canonicalEmergencies.map(entry => ({ ...entry }))

  return {
    ...state,
    club: {
      ...state.club,
      resources: { ...state.club.resources },
      players: state.club.players.map((entry) => {
        if (entry.id === ariId) {
          return {
            ...entry,
            isSelected: debut,
            positionInTeam: debut ? 'INT' : null,
            roleInTeam: debut ? 'inside midfielder' : null,
            staffConcern: debut
              ? 'Coach override: senior debut selected; pace and development opportunity rise while tactical reliability falls.'
              : 'Approaching senior readiness; a debut trades reliability for pace and senior exposure.',
          }
        }
        if (entry.id === heathId) {
          // When Ari is selected, Heath is unavailable (not selected).
          // When restoring staff plan, Heath's selection state is derived from
          // the canonical staff defaults interchange, not hard-coded.
          const heathSelected = !debut && canonicalInterchange.some(e => e.playerId === heathId)
          return {
            ...entry,
            isSelected: heathSelected,
            positionInTeam: heathSelected ? 'INT' : null,
            roleInTeam: heathSelected ? 'inside midfielder' : null,
          }
        }
        return { ...entry }
      }),
    },
    decisions: {
      ...state.decisions,
      D2: {
        ...state.decisions.D2,
        currentChoice: debut ? 'B' : 'A',
        isLocked: debut,
      },
    },
    staffPlan: {
      ...state.staffPlan,
      team: {
        ...state.staffPlan.team,
        interchange: debut ? selectedInterchange : restoredInterchange,
        emergencies: debut
          ? state.staffPlan.team.emergencies.filter((entry) => entry.playerId !== ariId).map((entry) => ({ ...entry }))
          : restoredEmergencies,
      },
    },
    week: state.week,
    matchResult: null,
    consequenceReport: null,
  }
}

export const setPhase = (state: GameState, phase: GamePhase): GameState => ({ ...state, phase })

const splitScore = (goals: number, behinds: number) => Array.from({ length: 4 }, (_, index) => ({
  goals: Math.floor(goals / 4) + (index < goals % 4 ? 1 : 0),
  behinds: Math.floor(behinds / 4) + (index < behinds % 4 ? 1 : 0),
}))

const scoreFromTotal = (total: number, seed: string) => {
  const safeTotal = Math.max(0, Math.round(total))
  const goals = Math.max(0, Math.min(Math.floor(safeTotal / 6), Math.round(safeTotal / 7 + seededNormal(seed, 'accuracy') * 0.8)))
  return { goals, behinds: safeTotal - goals * 6, total: safeTotal }
}

export const resolveGenericRound = (rawState: GameState): GameState => {
  const state = commitCurrentMatchContext(initialiseSeason(rawState))
  const season = state.season!
  const finalsFixture = state.round > 19
    ? season.finals?.fixtures.find(entry => entry.id === state.week?.fixtureId && !entry.result && (entry.homeTeamId === season.userTeamId || entry.awayTeamId === season.userTeamId))
    : undefined
  const fixture: ScheduledFixture = finalsFixture
    ? { id: finalsFixture.id, round: state.round, homeTeamId: finalsFixture.homeTeamId, awayTeamId: finalsFixture.awayTeamId, seed: stableHash(finalsFixture.id), venue: finalsFixture.venue, stage: finalsFixture.stage }
    : fixtureForTeam(season, season.userTeamId, state.round)
  const simulated = simulateLeagueFixture(season, fixture)
  const home = fixture.homeTeamId === season.userTeamId
  const opponentTeamId = home ? fixture.awayTeamId : fixture.homeTeamId
  const expectedMargin = expectedMatchMargin(season.teams[season.userTeamId].simulationRating, season.teams[opponentTeamId].simulationRating, home ? 'home' : 'away')
  const fixtureSeed = season.matchContexts?.[fixture.id]?.seed ?? `${season.seed}:${fixture.id}`
  const combinedTotal = simulated.homeScore.total + simulated.awayScore.total
  const ourTotal = Math.max(24, Math.round((combinedTotal + expectedMargin) / 2))
  const opponentTotal = Math.max(24, combinedTotal - ourTotal)
  const baseOur = scoreFromTotal(ourTotal, `${fixtureSeed}:kingsport`)
  const baseOpponent = scoreFromTotal(opponentTotal, `${fixtureSeed}:opponent`)
  const baseResult: MatchResult = {
    round: state.round,
    opponent: state.opponent.name,
    venue: home ? 'home' : 'away',
    ourScore: baseOur,
    opponentScore: baseOpponent,
    quarters: splitScore(baseOur.goals, baseOur.behinds).map((ourScore, index) => ({ quarter: index + 1, ourScore, opponentScore: splitScore(baseOpponent.goals, baseOpponent.behinds)[index], description: index === 2 ? `Kingsport applied its prepared response to ${state.opponent.name}.` : 'Both teams contested territory and protected their preferred shape.' })),
    momentumShifts: [{ quarter: 3, time: '09:20', description: `${state.conditionalInstruction?.id ?? 'D7-A'} monitored: ${state.conditionalInstruction?.condition ?? 'staff trigger'}`, impact: 'neutral' }],
    keyEvents: [{ quarter: 3, time: '09:20', player: 'Kingsport Imperials', type: 'tactical', description: state.conditionalInstruction?.action ?? 'Staff conditional response applied.' }],
    injuries: [],
    playerPerformances: [
      { player: selectedPlayerForStaffSlot(state, 'Marcus Vale'), rating: 7.4, disposals: 25, goals: 1, tackles: 7, marks: 4, notes: 'Led the contest and organised the midfield response.' },
      { player: selectedPlayerForStaffSlot(state, 'Heath Mercer'), rating: 6.8, disposals: 17, goals: 0, tackles: 5, marks: 2, notes: 'Provided a reliable interchange rotation.' },
      { player: selectedPlayerForStaffSlot(state, 'Lachlan Rook'), rating: 7.1, disposals: 13, goals: 3, tackles: 2, marks: 7, notes: 'Provided a reliable target inside fifty.' },
      { player: selectedPlayerForStaffSlot(state, 'Theo Marr'), rating: 6.9, disposals: 19, goals: 0, tackles: 4, marks: 6, notes: 'Supported defensive transition and rebound.' },
    ],
    tacticalAnalysis: { ballMovementEffectiveness: 70, widthEffectiveness: 70, contestEffectiveness: 70, defensiveEffectiveness: 70, tempoEffectiveness: 70, summary: `Kingsport's staff plan engaged ${state.opponent.name}'s ${state.opponent.style.toLowerCase()} system.` },
  }
  let matchResult = withGeneratedInjuries(applyTacticalOverrides(baseResult, state), state)
  if (finalsFixture && matchResult.ourScore.total === matchResult.opponentScore.total) {
    const kingsportWinsExtraTime = stableHash(`${finalsFixture.id}:kingsport-extra-time`) % 2 === 0
    const ourScore = kingsportWinsExtraTime ? scoreFromTotal(matchResult.ourScore.total + 1, `${fixtureSeed}:extra-time:kingsport`) : matchResult.ourScore
    const opponentScore = kingsportWinsExtraTime ? matchResult.opponentScore : scoreFromTotal(matchResult.opponentScore.total + 1, `${fixtureSeed}:extra-time:opponent`)
    matchResult = {
      ...matchResult,
      ourScore,
      opponentScore,
      momentumShifts: [...matchResult.momentumShifts, { quarter: 4, time: 'Extra time', description: `${kingsportWinsExtraTime ? 'Kingsport' : state.opponent.name} broke the deadlock in deterministic extra time.`, impact: kingsportWinsExtraTime ? 'positive' : 'negative' }],
      keyEvents: [...matchResult.keyEvents, { quarter: 4, time: 'Extra time', player: kingsportWinsExtraTime ? 'Kingsport Imperials' : state.opponent.name, type: 'behind', description: 'The final was decided after scores were level at the end of regulation.' }],
    }
  }
  const ourQuarters = splitScore(matchResult.ourScore.goals, matchResult.ourScore.behinds)
  const opponentQuarters = splitScore(matchResult.opponentScore.goals, matchResult.opponentScore.behinds)
  matchResult.quarters = matchResult.quarters.map((quarter, index) => ({ ...quarter, ourScore: ourQuarters[index], opponentScore: opponentQuarters[index] }))
  const won = matchResult.ourScore.total > matchResult.opponentScore.total
  const drew = matchResult.ourScore.total === matchResult.opponentScore.total
  const development = buildWeeklyDevelopmentOutcomes(state, state.round)
  const nextResearch = Math.min(state.club.maxResearchCapacity, state.club.resources.research + state.club.weeklyResearchGeneration)
  const selected = new Set(Object.values(state.staffPlan.team).flat().filter(player => player.position !== 'EMG').map(player => player.playerId))
  const consequenceReport: ConsequenceReport = {
    resourceLedger: { ...state.club.resources, research: nextResearch, development: state.club.developmentCapacity, institutional: Math.max(0, Math.min(100, state.club.resources.institutional + (won ? 1 : drew ? 0 : -1))) },
    resourceChanges: { cash: 0, research: nextResearch - state.club.resources.research, development: 0, institutional: won ? 1 : drew ? 0 : -1 },
    playerConditionChanges: state.club.players.filter(player => selected.has(player.id)).map(player =>
      ({ player: player.name, fitnessChange: -3, fatigueChange: player.id === 'ari-dane' ? 7 : 6, formChange: won ? 2 : drew ? 0 : -1, moraleChange: won ? 2 : drew ? 0 : -1 })),
    developmentProgress: development.players,
    developmentPrograms: development.programs,
    injuries: matchResult.injuries,
    moraleChanges: [{
      player: 'Ari Dane',
      change: selected.has('ari-dane') ? (won ? 6 : drew ? 3 : 1) : (won ? 2 : drew ? 0 : -1),
      reason: selected.has('ari-dane')
        ? won ? 'Made a successful senior appearance.' : drew ? 'Earned senior exposure in a drawn match.' : 'The senior opportunity accelerated his development despite the result.'
        : 'Continued progress in the staff development plan.',
    }],
    institutionalChanges: [{ group: won ? 'supporters' : 'board', change: won ? 1 : drew ? 0 : -1, reason: won ? 'Another win strengthened confidence.' : drew ? 'The draw produced no material change in institutional confidence.' : 'The loss increased scrutiny without creating a crisis.' }],
    researchKnowledge: [{ subject: state.opponent.name, domain: 'system response', previousConfidence: 'medium', newConfidence: 'high', summary: `Kingsport recorded how ${state.opponent.name} responded to the selected match plan.` }],
    leagueStories: [{ headline: won ? 'Kingsport continues its season build' : drew ? `Kingsport and ${state.opponent.name} share the points` : `${state.opponent.name} defeats Kingsport`, summary: `Round ${state.round} is complete and the league table has updated.`, relevance: 'high' }],
    nextWeekStaffPlanChanges: [{ area: 'Weekly preparation', description: 'Staff will generate a fresh opponent-specific plan for the next scheduled fixture.' }],
  }
  const resolved = { ...state, matchResult, consequenceReport, phase: 'consequences' as const }
  return syncCareerHistory(finalsFixture
    ? { ...resolved, season: recordFinalsResult(season, finalsFixture.id, matchResult) }
    : recordLeagueRound(resolved, matchResult))
}

export const completeByeWeek = (rawState: GameState): GameState => {
  const state = initialiseSeason(rawState)
  if (state.phase === 'next-round') return state
  const season = state.season!
  const key = `s${season.season}-r${state.round}`
  if (state.week?.venue !== 'bye' || season.processedByeRounds?.includes(key)) return state
  const finalRound = Math.max(...season.fixtures.map(fixture => fixture.round))
  let advancedSeason = recordAiRound(season, state.round)
  advancedSeason = {
    ...advancedSeason,
    completedThroughRound: Math.max(advancedSeason.completedThroughRound, state.round),
    processedByeRounds: [...(advancedSeason.processedByeRounds ?? []), key],
  }
  if (state.round >= finalRound) advancedSeason = completeSeasonFinals(advancedSeason)

  const development = buildWeeklyDevelopmentOutcomes(state, state.round, false)
  const progressed = applyDevelopmentProgress(state.club.players, development.players, state.round)
  const recovered = applyWeeklyInjuries(progressed.map(player => ({
    ...player,
    fitness: Math.min(100, player.fitness + 3),
    fatigue: Math.max(0, player.fatigue - 6),
  })), [])
  const nextResearch = Math.min(state.club.maxResearchCapacity, state.club.resources.research + state.club.weeklyResearchGeneration)
  let transitioned: GameState = repairUnavailableSelections({
    ...state,
    round: state.round,
    phase: 'next-round',
    season: advancedSeason,
    matchResult: null,
    consequenceReport: null,
    researchPlan: { ...state.researchPlan, openingCapacity: nextResearch },
    club: {
      ...state.club,
      resources: { ...state.club.resources, research: nextResearch },
      researchCapacity: nextResearch,
      tacticalCohesion: state.club.tacticalCohesion + tacticalCohesionGain(state),
      players: recovered,
    },
  })
  transitioned = advanceFacilityConstruction(transitioned, state.round)
  transitioned = updateClubStrategyAfterRound(transitioned, state.round)
  const completedRoundView = { ...transitioned, round: state.round }
  transitioned = processWeeklyRevenue(completedRoundView)
  transitioned = processStaffPoaching(processNegotiationRound(transitioned), state.round)
  return syncCareerHistory({ ...transitioned, round: state.round >= finalRound ? finalRound + 1 : state.round + 1 })
}

const completeSingleFinalsWeek = (state: GameState): GameState => {
  const season = advanceSeasonFinalsWeek(state.season!)
  const complete = season.finals?.status === 'complete'
  const nextResearch = Math.min(state.club.maxResearchCapacity, state.club.resources.research + state.club.weeklyResearchGeneration)
  const progressed: GameState = {
    ...state,
    round: complete ? 23 : Math.min(23, state.round + 1),
    phase: 'next-round',
    season,
    matchResult: null,
    consequenceReport: null,
    club: { ...state.club, resources: { ...state.club.resources, research: nextResearch }, researchCapacity: nextResearch },
  }
  const withFacilities = advanceFacilityConstruction(progressed, state.round)
  const completedWeekView = { ...withFacilities, round: state.round }
  const withRevenue = processWeeklyRevenue(completedWeekView)
  const processed = processStaffPoaching(processNegotiationRound(withRevenue), state.round)
  return { ...syncCareerHistory(processed), round: progressed.round }
}

export const completeFinalsWeek = (state: GameState): GameState => {
  if (state.phase === 'next-round' || state.round < 20 || state.season?.finals?.status !== 'active') return state
  const userTeamId = state.season.userTeamId
  if (isTeamAliveInFinals(state.season.finals, userTeamId)) return completeSingleFinalsWeek(state)

  // Once Kingsport has missed or exited the finals, compress the remaining AI
  // tournament into one action while still applying every weekly club process.
  let current = state
  while (current.season?.finals?.status === 'active') {
    current = completeSingleFinalsWeek(current)
    if (current.season?.finals?.status === 'complete') break
    current = prepareFinalsWeek(current)
  }
  return current
}

export const resolveCurrentRound = (state: GameState): GameState => {
  if (hasPendingLeftFieldEvent(state)) return state
  if (state.matchResult && state.consequenceReport) return { ...state, phase: 'consequences' }
  if (state.week?.venue === 'bye') return state.round > 19 ? completeFinalsWeek(state) : completeByeWeek(state)
  if (teamSelectionIssue(state.staffPlan.team, state.club.players)) return state
  return resolveGenericRound(state)
}

const applyGenericRound = (state: GameState): GameState => {
  const resolved = state.matchResult && state.consequenceReport ? state : resolveGenericRound(state)
  const season = resolved.season!
  const finalRound = Math.max(...season.fixtures.map(fixture => fixture.round))
  const isFinals = resolved.round > finalRound
    && season.finals?.status === 'active'
    && Boolean(season.finals.fixtures.find(fixture => fixture.id === resolved.week?.fixtureId)?.result)
  if (!isFinals && season.completedThroughRound >= finalRound) return { ...resolved, round: finalRound, phase: 'next-round' }
  const won = resolved.matchResult!.ourScore.total > resolved.matchResult!.opponentScore.total
  const drew = resolved.matchResult!.ourScore.total === resolved.matchResult!.opponentScore.total
  const progressed = applyDevelopmentProgress(resolved.club.players, resolved.consequenceReport!.developmentProgress, resolved.round)
  const conditionedPlayers = progressed.map(player => {
    const change = resolved.consequenceReport!.playerConditionChanges.find(item => item.player === player.name)
    return change ? { ...player, fitness: Math.max(0, Math.min(100, player.fitness + change.fitnessChange)), fatigue: Math.max(0, Math.min(100, player.fatigue + change.fatigueChange)), form: Math.max(0, Math.min(100, player.form + change.formChange)), morale: Math.max(0, Math.min(100, player.morale + change.moraleChange)) } : player
  })
  const players = applyWeeklyInjuries(conditionedPlayers, resolved.matchResult!.injuries)
  const nextCompletedThroughRound = isFinals ? season.completedThroughRound : resolved.round
  const isFinalRound = !isFinals && nextCompletedThroughRound >= finalRound
  const nextSeason = isFinals
    ? advanceSeasonFinalsWeek(season)
    : completeSeasonFinals({ ...season, completedThroughRound: nextCompletedThroughRound })
  const finalsComplete = nextSeason.finals?.status === 'complete'
  return syncCareerHistory({
    ...resolved,
    round: isFinals ? (finalsComplete ? 23 : Math.min(23, resolved.round + 1)) : isFinalRound ? finalRound + 1 : resolved.round + 1,
    phase: 'next-round',
    season: nextSeason,
    researchArchive: archiveCurrentResearch(resolved),
    researchPlan: { ...resolved.researchPlan, openingCapacity: resolved.consequenceReport!.resourceLedger.research },
    club: {
      ...resolved.club,
      record: isFinals ? resolved.club.record : { wins: resolved.club.record.wins + (won ? 1 : 0), losses: resolved.club.record.losses + (!won && !drew ? 1 : 0), draws: resolved.club.record.draws + (drew ? 1 : 0) },
      resources: { ...resolved.consequenceReport!.resourceLedger },
      researchCapacity: resolved.consequenceReport!.resourceLedger.research,
      tacticalCohesion: resolved.club.tacticalCohesion + tacticalCohesionGain(resolved),
      players,
    },
  })
}

export const advanceAfterRound = (state: GameState): GameState => {
  if (state.phase === 'next-round' || hasPendingLeftFieldEvent(state)) return state
  const resolved = state.matchResult && state.consequenceReport ? state : resolveCurrentRound(state)
  if (resolved.phase === 'next-round') return resolved
  const updated = applyGenericRound(resolved)
  const withFacilities = advanceFacilityConstruction(updated, resolved.round)
  const withStrategy = updateClubStrategyAfterRound(withFacilities)
  // Weekly ledgers and negotiations belong to the round just completed, even
  // though the handover state already points at the next round.
  const completedRoundView = { ...withStrategy, round: resolved.round }
  const withWeeklyRevenue = processWeeklyRevenue(completedRoundView)
  const processed = processStaffPoaching(processNegotiationRound(withWeeklyRevenue), resolved.round)
  return { ...processed, round: updated.round }
}

export const continueToNextRound = (state: GameState): GameState => {
  if (state.phase !== 'next-round') return state
  const season = state.season!
  const finalRound = Math.max(...season.fixtures.map(fixture => fixture.round))
  if (season.completedThroughRound >= finalRound) {
    if (season.finals?.status === 'active') {
      const prepared = prepareFinalsWeek(state)
      return isTeamAliveInFinals(season.finals, season.userTeamId) ? prepared : completeFinalsWeek(prepared)
    }
    return state
  }
  return prepareLeftFieldEvent(prepareSeasonRound(state, state.round))
}

export const createStaffDevelopmentPlan = (devPlans: import('./types').DevelopmentAllocation[] = _getCanonStaffPlans()): import('./types').DevelopmentAllocation[] => devPlans

const _getCanonStaffPlans = (): import('./types').DevelopmentAllocation[] => [
  { playerId: slug('Ari Dane'), focus: 'tactical discipline', points: 3, isLocked: false, assistant: 'mara-keane' },
  { playerId: 'young-player-group', focus: 'general development', points: 2, isLocked: false },
  { playerId: 'tactical-cohesion', focus: 'team cohesion', points: 2, isLocked: false },
  { playerId: 'veteran-recovery', focus: 'recovery', points: 2, isLocked: false },
  { playerId: slug('Theo Marr'), focus: 'defensive-wing retraining', points: 1, isLocked: false, assistant: 'grant-havel' },
]

const slug = (name: string) => name.toLowerCase().replaceAll(' ', '-')

export const conditionalInstruction = (opponentName = 'Opponent'): import('./types').ConditionalInstruction => ({
  id: 'D7-A',
  label: 'Increase width if stoppages are lost',
  description: `Move the game away from ${opponentName}'s contested strength.`,
  condition: `${opponentName} wins repeated stoppages`,
  action: 'Increase attacking width',
  isAvailable: true,
  isSelected: true,
})

/**
 * Commits the deterministic match context and locks all weekly decisions.
 * This is the "Start Match" commitment transaction (steps 3-5 of the spec).
 * Must be called BEFORE saving the matchday autosave to ensure the checkpoint
 * contains the committed context and locked decisions for deterministic replay.
 */
export const commitAndLockForMatch = (state: GameState): GameState => {
  if (!isReadyForMatch(state)) {
    throw new Error('Cannot commit match: required weekly actions are not resolved.')
  }

  // Step 3: Commit deterministic match context
  const withCommittedContext = commitCurrentMatchContext(initialiseSeason(state))

  // Step 5: Lock the week's decisions
  const lockedDecisions = {} as GameState['decisions']
  for (const [id, decision] of Object.entries(withCommittedContext.decisions)) {
    lockedDecisions[id as keyof GameState['decisions']] = {
      ...decision,
      isLocked: true,
    }
  }

  // Also lock development allocations
  const lockedDevelopment = withCommittedContext.staffPlan.development.map(allocation => ({
    ...allocation,
    isLocked: true,
  }))

  return {
    ...withCommittedContext,
    decisions: lockedDecisions,
    staffPlan: {
      ...withCommittedContext.staffPlan,
      development: lockedDevelopment,
    },
  }
}